import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import { fetchHistoricalTimeline } from "../../../../services/insightApp/historicalAnalytics";
import { formatDuration, formatTime } from "./historicalUtils";

/**
 * INSIGHT-HIST-16 — one order's historical sequence.
 *
 * Events are shown in the order they happened, with the completed durations
 * and the final order record when there is one. Gaps stay gaps: an order with
 * no delivery event simply has no delivery row, and an order that never
 * completed has no final record.
 */

export interface OrderTimelineModalProps {
  open: boolean;
  onClose: () => void;
  locationId: string;
  orderNo: string;
  businessDate: string;
}

const OrderTimelineModal: React.FunctionComponent<OrderTimelineModalProps> = ({
  open,
  onClose,
  locationId,
  orderNo,
  businessDate,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!open || !orderNo || !businessDate) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError("");

      try {
        const res = await fetchHistoricalTimeline(locationId, {
          orderNo,
          businessDate,
        });

        if (!cancelled) {
          setData(res?.data?.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError("The order timeline could not be loaded.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, locationId, orderNo, businessDate]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Order {orderNo} — {businessDate}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !data || !data.hasData ? (
          <Typography variant="body2" color="textSecondary">
            No historical events were recorded for this order.
          </Typography>
        ) : (
          <>
            {data.order && (
              <Box mb={2}>
                <Typography variant="subtitle2">Order</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      Received
                    </Typography>
                    <Typography variant="body2">
                      {formatTime(data.order.receivedAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      Completed
                    </Typography>
                    <Typography variant="body2">
                      {formatTime(data.order.completedAt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      Mode
                    </Typography>
                    <Typography variant="body2">
                      {data.order.mode || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">
                      Driver
                    </Typography>
                    <Typography variant="body2">
                      {data.order.driverId || "-"}
                    </Typography>
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <Divider />
                </Box>
              </Box>
            )}

            {data.metrics && data.metrics.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Completed durations
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Metric</TableCell>
                      <TableCell>Station</TableCell>
                      <TableCell align="right">Duration</TableCell>
                      <TableCell align="right">Target</TableCell>
                      <TableCell>Result</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.metrics.map((metric: any) => (
                      <TableRow
                        key={`${metric.metricType}-${metric.stationId || ""}`}
                      >
                        <TableCell>{metric.metricType}</TableCell>
                        <TableCell>{metric.stationId || "-"}</TableCell>
                        <TableCell align="right">
                          {formatDuration(metric.durationMs)}
                        </TableCell>
                        <TableCell align="right">
                          {formatDuration(metric.targetMs)}
                        </TableCell>
                        <TableCell>
                          {metric.withinTarget === null ||
                          metric.withinTarget === undefined ? (
                            <Chip size="small" label="No target" />
                          ) : metric.withinTarget ? (
                            <Chip size="small" label="Within target" />
                          ) : (
                            <Chip size="small" color="secondary" label="Late" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom>
              Events
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Station</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Device</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.events.map((event: any, index: number) => (
                  <TableRow key={`${event.eventType}-${event.eventAt}-${index}`}>
                    <TableCell>{formatTime(event.eventAt)}</TableCell>
                    <TableCell>{event.eventType}</TableCell>
                    <TableCell>{event.stationId || "-"}</TableCell>
                    <TableCell>{event.driverId || "-"}</TableCell>
                    <TableCell>{event.deviceId || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderTimelineModal;
